import lodashMerge from 'lodash/merge'

import type { BusinessData } from 'types/business'
import type { AccountPlan } from 'types/business2020'
import type { Location } from 'react-router-dom'

import type { PlanId } from '@disco/utils/featuresAccessLevel'

import { ARTIST_ID, PLUS_ID, PRO_ID } from '@disco/utils/featuresAccessLevel'

/**
 * Maximum track quota used for unlimited plans (frontend display only).
 *
 * This value is used for UI rendering purposes only and should not be sent
 * to billing systems like Chargebee. It represents "unlimited" in the UI.
 *
 * @example
 * ```typescript
 * const plan = {
 *   files: {
 *     defaultCount: UNLIMITED_TRACKS_QUOTA // Shows as "unlimited" in UI
 *   }
 * }
 * ```
 */
export const UNLIMITED_TRACKS_QUOTA = 99999

/**
 * Array of available pricing plans with their configurations.
 *
 * Contains all active subscription plans including pricing, quotas, features,
 * and UI styling information. Used throughout the application for plan
 * comparison, billing, and feature access control.
 *
 * @example
 * ```typescript
 * // Find a specific plan
 * const proPlan = pricingPlans.find(plan => plan.handle === PRO_ID)
 *
 * // Get recommended plan
 * const recommended = pricingPlans.find(plan => plan.recommended)
 *
 * // Display all plans in UI
 * pricingPlans.map(plan => (
 *   <PlanCard key={plan.handle} plan={plan} />
 * ))
 * ```
 */
export const pricingPlans: AccountPlan[] = [
  // Deprecated Lite plan, is hidden from the UI. Server-side validation in the Plan Change API will be done as a followup
  // {
  //   name: 'Lite',
  //   handle: LITE_ID,
  //   cost: 10,
  //   users: {
  //     defaultCount: 1,
  //   },
  //   files: {
  //     defaultCount: 500,
  //   },
  //   chosenColor: {
  //     line: {
  //       backgroundColor: '#face2f',
  //     },
  //     badge: {
  //       backgroundColor: '#ffab00',
  //       color: '#172b4d',
  //     },
  //     boxShadow: {
  //       boxShadow: 'inset 0 0 1px 1px #face2f',
  //     },
  //   },
  //   topFeatures: [
  //     'Unlimited playlists',
  //     'Embeddable tracks, videos and playlists',
  //     'Free catalog import',
  //     'Inbox for receiving files',
  //   ],
  //   recommended: false,
  // },
  {
    name: 'Plus',
    handle: PLUS_ID,
    cost: 18.99, // TODO: switch depending on feature flag
    annualCost: 16.99, // 16.99 = 18.99 * 0.9 rounded down to nearest .99
    changeQuota: 1,
    users: {
      defaultCount: 1,
      max: 10,
      available: 15,
      cost: 18.99,
      annualCost: 16.99,
    },
    files: {
      defaultCount: 1000,
      max: 20000,
      available: 30000,
      cost: 0.007,
      annualCost: 0.007 * 0.9,
    },
    chosenColor: {
      line: {
        backgroundColor: '#37b57b',
      },
      badge: {
        backgroundColor: '#27875a',
        color: '#ffffff',
      },
      boxShadow: {
        boxShadow: 'inset 0 0 1px 1px #37b57b',
      },
    },
    featuresTitle: null,
    topFeatures: [
      'Unlimited playlists',
      'Email Creator',
      'Advanced playlist stats',
      'Assigned URLs',
    ],
    recommended: false,
  },
  {
    name: 'Pro',
    handle: PRO_ID,
    cost: 29.99,
    annualCost: 26.99, // 26.99 = 29.99 * 0.9 rounded to cents
    changeQuota: 1,
    users: {
      defaultCount: 1,
      max: 10,
      available: 15,
      cost: 29.99,
      annualCost: 29.99 * 0.9,
    },
    files: {
      defaultCount: 1000,
      max: 20000,
      available: 30000,
      cost: 0.007,
      annualCost: 0.007 * 0.9,
    },
    description: 'Have more than 2500 tracks, or are a music supervisor?',
    chosenColor: {
      line: {
        backgroundColor: '#2b79ce',
      },
      badge: {
        backgroundColor: '#2151cc',
        color: '#ffffff',
      },
      boxShadow: {
        boxShadow: 'inset 0 0 1px 1px #2b79ce',
      },
    },
    featuresTitle: 'Everything on Plus, as well as:',
    topFeatures: [
      'Custom fields',
      'Password protection',
      'Distributor Ingestion ',
    ],
    recommended: true,
  },
  {
    name: 'Artist',
    handle: ARTIST_ID,
    cost: 12,
    annualCost: 12 * 0.9,
    users: {
      defaultCount: 2,
    },
    files: {
      defaultCount: UNLIMITED_TRACKS_QUOTA,
    },
    chosenColor: {
      line: {
        backgroundColor: '#face2f',
      },
      badge: {
        backgroundColor: '#ffab00',
        color: '#172b4d',
      },
      boxShadow: {
        boxShadow: 'inset 0 0 1px 1px #face2f',
      },
    },
    topFeatures: [
      'Unlimited tracks, playlists, channels',
      'Advanced Playlist Stats / Assigned URLs',
      'Playlist security',
      'WAV → AIFF Conversion',
      'Autotagging',
    ],
    recommended: false,
    autoSelect: true,
  },
]

export const legacyPricingPlans = lodashMerge([], pricingPlans, [
  {
    // plus
    cost: 15,
    annualCost: 15 * 0.9,
    users: {
      cost: 15,
      annualCost: 15 * 0.9,
    },
    files: {
      cost: 0.007,
      annualCost: 0.007 * 0.9,
    },
  },
  {
    // pro
    cost: 25,
    annualCost: 25 * 0.9,
    users: {
      cost: 25,
      annualCost: 25 * 0.9,
    },
    files: {
      cost: 0.007,
      annualCost: 0.007 * 0.9,
    },
  },
])

/**
 * Converts a number to a string with proper decimal formatting.
 *
 * Ensures consistent formatting for monetary values by showing decimal
 * places only when necessary (for non-integer values).
 *
 * @param number - The number to convert to cents format
 * @returns String representation with appropriate decimal places
 *
 * @example
 * ```typescript
 * toCents(15) // "15"
 * toCents(15.99) // "15.99"
 * toCents(15.00) // "15"
 * toCents(15.5) // "15.50"
 * ```
 */
export const toCents = (number: number): string => {
  // TODO: replace with formatCurrency
  if (!Number.isInteger(number)) {
    return String(number.toFixed(2))
  }
  return String(number)
}

export function formatCurrency(cents) {
  // Ensure cents is a number
  if (cents === null || cents === undefined) {
    return cents || '0'
  }

  return (
    (cents >= 0 ? '' : '-') +
    '$' +
    (Math.abs(cents) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  )
}

/** Valid billing page root paths for navigation. */
export type BillingRootPath =
  | '/pricing'
  | '/settings/billing'
  | '/settings/billing/plans'

/**
 * Determines the appropriate billing root path based on current location.
 *
 * Routes users to either the public pricing page or the settings billing
 * section based on their current URL path.
 *
 * @param location - Current browser location object
 * @returns Appropriate billing root path for navigation
 *
 * @example
 * ```typescript
 * const location = { pathname: '/pricing/plans' }
 * const rootPath = getRootPath(location) // '/pricing'
 *
 * const settingsLocation = { pathname: '/settings/billing/change' }
 * const settingsPath = getRootPath(settingsLocation) // '/settings/billing'
 * ```
 */
export const getRootPath = (location: Location): BillingRootPath => {
  return location.pathname.startsWith('/pricing')
    ? '/pricing'
    : '/settings/billing'
}

/**
 * Gets default quotas for users and files based on plan ID.
 *
 * Looks up the specified plan and returns its default quotas, falling
 * back to sensible defaults if the plan is not found.
 *
 * @param corePlanId - Plan identifier to look up quotas for
 * @returns Object containing default user and file quotas
 *
 * @example
 * ```typescript
 * const quotas = getDefaultPlanQuotas(PRO_ID)
 * // { users: 1, files: 1000 }
 *
 * const unknownPlan = getDefaultPlanQuotas('unknown-plan')
 * // { users: 1, files: 1000 } - fallback defaults
 *
 * const artistQuotas = getDefaultPlanQuotas(ARTIST_ID)
 * // { users: 2, files: 99999 }
 * ```
 */
export const getDefaultPlanQuotas = (
  corePlanId?: PlanId | string,
): {
  users: number
  files: number
} => {
  const result = {
    users: 1,
    files: 1000,
  }

  const corePlan = corePlanId
    ? pricingPlans.find(plan => {
        return plan.handle?.includes(corePlanId)
      })
    : null

  if (corePlan) {
    result.users = corePlan.users.defaultCount
    result.files = corePlan.files.defaultCount
  }

  return result
}

/**
 * Gets current quota limits for enterprise businesses.
 *
 * Extracts custom user and track allowances from business metadata,
 * falling back to Infinity for unlimited access if no custom limits
 * are set.
 *
 * @param business - Business data containing enterprise metadata
 * @returns Object with maximum users and tracks allowed
 *
 * @example
 * ```typescript
 * const business = {
 *   private_metadata: {
 *     enterprise_user_allowance: '50',
 *     enterprise_track_allowance: '10000'
 *   }
 * }
 *
 * const quota = getCurrentQuota(business)
 * // { maxUsers: 50, maxTracks: 10000 }
 *
 * // Business without custom limits
 * const unlimitedBusiness = { private_metadata: {} }
 * const unlimitedQuota = getCurrentQuota(unlimitedBusiness)
 * // { maxUsers: Infinity, maxTracks: Infinity }
 * ```
 */
export function getCurrentQuota(business: BusinessData): {
  maxUsers: number
  maxTracks: number
} {
  const {
    private_metadata: { enterprise_user_allowance, enterprise_track_allowance },
  } = business

  let maxUsers = Infinity
  let maxTracks = Infinity

  const customUserAllowance = parseInt(enterprise_user_allowance || '')
  const customTrackAllowance = parseInt(enterprise_track_allowance || '')

  if (!isNaN(customUserAllowance)) {
    maxUsers = customUserAllowance
  }
  if (!isNaN(customTrackAllowance)) {
    maxTracks = customTrackAllowance
  }

  return {
    maxUsers,
    maxTracks,
  }
}
